import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'

const getSupabaseUrl = () => {
  const url = process.env.REACT_APP_SUPABASE_URL
  if (!url) {
    throw new Error('REACT_APP_SUPABASE_URL environment variable is not configured')
  }
  return url
}
type PermissionLevel = 'read_only' | 'contributor' | 'full_access'

interface Invitation {
  id: string
  inviter_id: string
  invitee_email: string
  permission_level: PermissionLevel
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  token: string
  expires_at: string
  created_at: string
  updated_at: string
  profiles?: {
    display_name: string
  }
}

interface CollectionShare {
  id: string
  owner_id: string
  shared_with_id: string
  permission_level: PermissionLevel
  created_at: string
  updated_at: string
  profiles?: {
    display_name: string
  }
}

interface InvitationManagementResponse {
  success: boolean
  data?: any
  error?: string
  invitation?: Invitation
  inviteLink?: string
  share?: CollectionShare
}

interface SharesListResponse extends InvitationManagementResponse {
  data?: {
    owned: CollectionShare[]
    received: CollectionShare[]
  }
}

const PermissionLevelLabels: Record<PermissionLevel, string> = {
  read_only: 'View Only',
  contributor: 'Can Add Items',
  full_access: 'Full Access'
}

const PermissionLevelDescriptions: Record<PermissionLevel, string> = {
  read_only: 'Can view storage bins and cookie cutters only',
  contributor: 'Can view and add new storage bins and cookie cutters',
  full_access: 'Full access including ability to delete items'
}

const Sharing: React.FC = () => {
  const { session } = useAuth()
  const [activeTab, setActiveTab] = useState<'invitations' | 'shares'>('invitations')
  const [sentInvitations, setSentInvitations] = useState<Invitation[]>([])
  const [receivedInvitations, setReceivedInvitations] = useState<Invitation[]>([])
  const [ownedShares, setOwnedShares] = useState<CollectionShare[]>([])
  const [receivedShares, setReceivedShares] = useState<CollectionShare[]>([])
  const [loading, setLoading] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [invitePermission, setInvitePermission] = useState<PermissionLevel>('read_only')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      if (!session) {
        setError('Not authenticated')
        return
      }

      const token = session.access_token

      if (activeTab === 'invitations') {
        // Load sent invitations
        const sentResponse = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'list', type: 'sent' })
        })

        const sentData: InvitationManagementResponse = await sentResponse.json()
        if (sentData.success) {
          setSentInvitations(sentData.data || [])
        }

        // Load received invitations
        const receivedResponse = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'list', type: 'received' })
        })

        const receivedData: InvitationManagementResponse = await receivedResponse.json()
        if (receivedData.success) {
          setReceivedInvitations(receivedData.data || [])
        }
      } else {
        // Load shares
        const sharesResponse = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'list', type: 'shares' })
        })

        const sharesData: SharesListResponse = await sharesResponse.json()
        if (sharesData.success && sharesData.data) {
          setOwnedShares(sharesData.data.owned || [])
          setReceivedShares(sharesData.data.received || [])
        }
      }
    } catch (err: any) {
      console.error('API Error:', err)
      if (err.message && err.message.includes('Failed to fetch')) {
        setError('Sharing system is not yet configured. Please deploy the manage-invitations Edge Function to Supabase.')
      } else {
        setError(err.message || 'Failed to load data')
      }
    } finally {
      setLoading(false)
    }
  }, [activeTab, session])

  useEffect(() => {
    loadData()
  }, [loadData])

  const sendInvitation = async () => {
    if (!inviteEmail.trim()) {
      setError('Email is required')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      if (!session) {
        setError('Not authenticated')
        return
      }
      
      const token = session.access_token

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'send',
          inviteeEmail: inviteEmail,
          permissionLevel: invitePermission
        })
      })

      const data: InvitationManagementResponse = await response.json()
      
      if (data.success) {
        setSuccess(`Invitation sent successfully! Share this link: ${data.inviteLink}`)
        setShowInviteModal(false)
        setInviteEmail('')
        setInvitePermission('read_only')
        loadData()
      } else {
        setError(data.error || 'Failed to send invitation')
      }
    } catch (err: any) {
      console.error('Send invitation error:', err)
      if (err.message && err.message.includes('Failed to fetch')) {
        setError('Sharing system is not yet configured. Please deploy the manage-invitations Edge Function to Supabase.')
      } else {
        setError(err.message || 'Failed to send invitation')
      }
    } finally {
      setLoading(false)
    }
  }

  const revokeInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) return

    setLoading(true)
    try {
      if (!session) {
        setError('Not authenticated')
        return
      }
      
      const token = session.access_token

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'revoke',
          invitationId
        })
      })

      const data: InvitationManagementResponse = await response.json()
      
      if (data.success) {
        setSuccess('Invitation revoked successfully')
        loadData()
      } else {
        setError(data.error || 'Failed to revoke invitation')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to revoke invitation')
    } finally {
      setLoading(false)
    }
  }

  const removeAccess = async (shareId: string) => {
    if (!window.confirm('Are you sure you want to remove this access?')) return

    setLoading(true)
    try {
      if (!session) {
        setError('Not authenticated')
        return
      }
      
      const token = session.access_token

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'remove_access',
          shareId
        })
      })

      const data: InvitationManagementResponse = await response.json()
      
      if (data.success) {
        setSuccess('Access removed successfully')
        loadData()
      } else {
        setError(data.error || 'Failed to remove access')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove access')
    } finally {
      setLoading(false)
    }
  }

  const updatePermission = async (shareId: string, newPermission: PermissionLevel) => {
    setLoading(true)
    try {
      if (!session) {
        setError('Not authenticated')
        return
      }
      
      const token = session.access_token

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'update_permission',
          shareId,
          permissionLevel: newPermission
        })
      })

      const data: InvitationManagementResponse = await response.json()
      
      if (data.success) {
        setSuccess('Permissions updated successfully')
        loadData()
      } else {
        setError(data.error || 'Failed to update permissions')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update permissions')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50'
      case 'accepted': return 'text-green-600 bg-green-50'
      case 'declined': return 'text-red-600 bg-red-50'
      case 'expired': return 'text-gray-600 bg-gray-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-battleship-gray">Collection Sharing</h1>
        <button
          onClick={() => setShowInviteModal(true)}
          className="bg-taupe hover:bg-battleship-gray text-white px-4 py-2 rounded-lg"
          disabled={loading}
        >
          Invite Someone
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-semibold mb-2">{error}</div>
          {error.includes('not yet configured') && (
            <div className="text-sm">
              <p className="mb-2">To enable the sharing system, you need to:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Install the Supabase CLI</li>
                <li>Run: <code className="bg-red-100 px-1 rounded">supabase functions deploy manage-invitations</code></li>
                <li>Apply the database schema from <code className="bg-red-100 px-1 rounded">invitation_system_schema.sql</code></li>
              </ol>
              <p className="mt-2">See <code className="bg-red-100 px-1 rounded">setup-invitation-system.md</code> for detailed instructions.</p>
            </div>
          )}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('invitations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invitations'
                ? 'border-taupe text-taupe'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Invitations
          </button>
          <button
            onClick={() => setActiveTab('shares')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'shares'
                ? 'border-taupe text-taupe'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Shares
          </button>
        </nav>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="text-4xl mb-4">🍪</div>
          <div className="text-gray-600">Loading...</div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && !loading && (
        <div className="space-y-8">
          {/* Sent Invitations */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-battleship-gray">Sent Invitations</h2>
            {sentInvitations.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No invitations sent yet</div>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sentInvitations.map((invitation) => (
                      <tr key={invitation.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invitation.invitee_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {PermissionLevelLabels[invitation.permission_level]}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invitation.status)}`}>
                            {invitation.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(invitation.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {invitation.status === 'pending' && (
                            <button
                              onClick={() => revokeInvitation(invitation.id)}
                              className="text-red-600 hover:text-red-900"
                              disabled={loading}
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Received Invitations */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-battleship-gray">Received Invitations</h2>
            {receivedInvitations.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No invitations received</div>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {receivedInvitations.map((invitation) => (
                      <tr key={invitation.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invitation.profiles?.display_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {PermissionLevelLabels[invitation.permission_level]}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invitation.status)}`}>
                            {invitation.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(invitation.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shares Tab */}
      {activeTab === 'shares' && !loading && (
        <div className="space-y-8">
          {/* People you've shared with */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-battleship-gray">People with access to your collection</h2>
            {ownedShares.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No one has access to your collection yet</div>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Person</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permission</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Since</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ownedShares.map((share) => (
                      <tr key={share.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {share.profiles?.display_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <select
                            value={share.permission_level}
                            onChange={(e) => updatePermission(share.id, e.target.value as PermissionLevel)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                            disabled={loading}
                          >
                            <option value="read_only">{PermissionLevelLabels.read_only}</option>
                            <option value="contributor">{PermissionLevelLabels.contributor}</option>
                            <option value="full_access">{PermissionLevelLabels.full_access}</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(share.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => removeAccess(share.id)}
                            className="text-red-600 hover:text-red-900"
                            disabled={loading}
                          >
                            Remove Access
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Collections shared with you */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-battleship-gray">Collections shared with you</h2>
            {receivedShares.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No collections have been shared with you</div>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your Permission</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Since</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {receivedShares.map((share) => (
                      <tr key={share.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {share.profiles?.display_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {PermissionLevelLabels[share.permission_level]}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(share.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => removeAccess(share.id)}
                            className="text-red-600 hover:text-red-900"
                            disabled={loading}
                          >
                            Leave
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Invite Someone to Your Collection</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-taupe"
                  placeholder="Enter email address"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Permission Level</label>
                <select
                  value={invitePermission}
                  onChange={(e) => setInvitePermission(e.target.value as PermissionLevel)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-taupe"
                >
                  <option value="read_only">{PermissionLevelLabels.read_only}</option>
                  <option value="contributor">{PermissionLevelLabels.contributor}</option>
                  <option value="full_access">{PermissionLevelLabels.full_access}</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  {PermissionLevelDescriptions[invitePermission]}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowInviteModal(false)
                    setInviteEmail('')
                    setInvitePermission('read_only')
                    setError('')
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={sendInvitation}
                  className="px-4 py-2 bg-taupe text-white rounded-md hover:bg-battleship-gray"
                  disabled={loading || !inviteEmail.trim()}
                >
                  Send Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Sharing